import { useState, useMemo } from "react";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ThemeAnalysisBlock } from "./ThemeAnalysisBlock";
import { CountBadge } from "@/components/shared/CountBadge";
import {
  Crown,
  MoreVertical,
  Trash2,
  Pencil,
  Plus,
  Loader2,
  ArrowUp,
  ArrowDown,
} from "lucide-react";
import { AISparkles } from "@/components/ui/ai-sparkles";
import type { ReactNode } from "react";

import { toast } from "@/hooks/use-toast";
import {
  useUpdateKeywordTheme,
  useDeleteKeywordTheme,
  useUpdateKeywordInTheme,
  useRemoveKeywordFromTheme,
  useRegenerateThemeAnalysis,
  keywordThemesQueryKey,
} from "@/resources/keyword-theme";
import type { KeywordTheme } from "@/types/keyword-theme";
import { formatMetricNumber, formatCompactNumber } from "@/utils/keywordMetricDisplay";
import { cn } from "@/lib/utils";
import { RankBadge, TrendCell, avg, sum, DeleteThemeDialog, KeywordMetricValue } from "./KeywordThemeShared";
import queryClient from "@/query-client";

interface ThemePanelProps {
  theme: KeywordTheme | null;
  productId: number;
  onClose: () => void;
  onAddKeyword?: () => void;
  onThemeUpdated?: (theme: KeywordTheme) => void;
}

export function ThemePanel({
  theme,
  productId,
  onClose,
  onAddKeyword,
  onThemeUpdated,
}: ThemePanelProps) {
  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState("");
  const [pendingKeywordId, setPendingKeywordId] = useState<number | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const { mutateAsync: updateTheme, isPending: isUpdating } =
    useUpdateKeywordTheme();
  const { mutateAsync: deleteTheme } = useDeleteKeywordTheme();
  const { mutateAsync: updateKeyword } = useUpdateKeywordInTheme();
  const { mutateAsync: removeKeyword } = useRemoveKeywordFromTheme();
  const { mutateAsync: regenerateAnalysis, isPending: isRegenerating } =
    useRegenerateThemeAnalysis();

  const stats = useMemo(() => {
    if (!theme) return null;
    const kws = theme.keywords;
    return {
      avgRank: avg(kws.map((k) => k.rank)),
      avgKps: avg(kws.map((k) => k.kps)),
      totalVol: sum(kws.map((k) => k.search_volume)),
      avgRel: avg(kws.map((k) => k.hero_relevance)),
      trendUp: kws.filter((k) => Number(k.search_volume_trend ?? 0) > 0).length,
      trendDown: kws.filter((k) => Number(k.search_volume_trend ?? 0) < 0).length,
    };
  }, [theme]);

  if (!theme) return null;

  const headTerm = theme.keywords.find((k) => k.is_head_term);

  const startEditingName = () => {
    setNameValue(theme.name);
    setEditingName(true);
  };

  const saveName = async () => {
    const trimmed = nameValue.trim();
    if (!trimmed || trimmed === theme.name) {
      setEditingName(false);
      return;
    }
    const updated = await updateTheme({
      productId,
      themeId: theme.id,
      name: trimmed,
    });
    onThemeUpdated?.(updated);
    setEditingName(false);
  };

  const handleDeleteTheme = async () => {
    setConfirmDelete(false);
    onClose();
    const queryKey = keywordThemesQueryKey(productId);
    const previous = queryClient.getQueryData<KeywordTheme[]>(queryKey);
    queryClient.setQueryData<KeywordTheme[]>(
      queryKey,
      (old) => old?.filter((t) => t.id !== theme.id) ?? old,
    );
    try {
      await deleteTheme({ productId, themeId: theme.id });
      toast({ title: "Theme deleted" });
    } catch {
      queryClient.setQueryData(queryKey, previous);
    }
  };

  const handleSetHeadTerm = async (keywordId: number) => {
    setPendingKeywordId(keywordId);
    try {
      await updateKeyword({
        productId,
        themeId: theme.id,
        keywordId,
        is_head_term: true,
      });
    } finally {
      setPendingKeywordId(null);
    }
  };

  const handleUnsetHeadTerm = async (keywordId: number) => {
    setPendingKeywordId(keywordId);
    try {
      await updateKeyword({
        productId,
        themeId: theme.id,
        keywordId,
        is_head_term: false,
      });
    } finally {
      setPendingKeywordId(null);
    }
  };

  const handleRemoveKeyword = async (keywordId: number) => {
    setPendingKeywordId(keywordId);
    try {
      await removeKeyword({ productId, themeId: theme.id, keywordId });
    } finally {
      setPendingKeywordId(null);
    }
  };

  const statItems: { label: string; prefix?: string; value?: string; node?: ReactNode }[] = [
    { label: "Rank",        prefix: "Avg", value: stats?.avgRank  != null ? `#${stats.avgRank}` : "—" },
    { label: "KPS",         prefix: "Avg", value: stats?.avgKps   != null ? String(stats.avgKps) : "—" },
    { label: "Search Vol.", prefix: "Agr", value: stats?.totalVol != null ? `${formatMetricNumber(stats.totalVol, formatCompactNumber)}/mo` : "—" },
    { label: "Rel.",        prefix: "Avg", value: stats?.avgRel   != null ? String(stats.avgRel) : "—" },
    {
      label: "Trend",
      node: theme.keywords.length > 0 ? (
        <span className="inline-flex items-center gap-2">
          <span className="inline-flex items-center gap-0.5 text-[hsl(var(--trend-positive))] tabular-nums">
            <ArrowUp className="h-3.5 w-3.5" strokeWidth={2.5} />
            {stats?.trendUp ?? 0}
          </span>
          <span className="inline-flex items-center gap-0.5 text-destructive tabular-nums">
            <ArrowDown className="h-3.5 w-3.5" strokeWidth={2.5} />
            {stats?.trendDown ?? 0}
          </span>
        </span>
      ) : <span className="text-muted-foreground">—</span>,
    },
  ];

  return (
    <>
      <Sheet
        open={!!theme}
        onOpenChange={(open) => {
          if (!open) onClose();
        }}
      >
        <SheetContent className="w-full sm:max-w-[640px] bg-card overflow-y-auto p-0">
          {/* Accessible title required by Radix's Dialog primitive underneath
              Sheet -- visually hidden since the panel already has its own
              custom heading below (which toggles into an <Input> while
              renaming, so it can't double as the one title Radix expects to
              always be present). */}
          <SheetTitle className="sr-only">{theme.name}</SheetTitle>
          <div className="p-6 space-y-6">
            {/* Header */}
            <div className="pr-8">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">
                Thematic Grouping
              </p>
              <div className="flex items-start justify-between gap-2">
                {editingName ? (
                  <form
                    className="flex items-center gap-2 flex-1"
                    onSubmit={(e) => {
                      e.preventDefault();
                      saveName();
                    }}
                  >
                    <Input
                      value={nameValue}
                      onChange={(e) => setNameValue(e.target.value)}
                      autoFocus
                      className="text-xl font-semibold h-auto py-1"
                    />
                    <Button type="submit" size="sm" disabled={isUpdating}>
                      Save
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => setEditingName(false)}
                    >
                      Cancel
                    </Button>
                  </form>
                ) : (
                  <>
                    <h2 className="text-2xl font-semibold flex-1 min-w-0 break-words">
                      {theme.name}
                    </h2>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 shrink-0"
                        >
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={startEditingName}>
                          <Pencil className="h-4 w-4 mr-2" />
                          Rename theme
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => setConfirmDelete(true)}
                          className="text-destructive"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete theme
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </>
                )}
              </div>
            </div>

            {/* Stats row */}
            <div className="grid grid-cols-5 border border-border rounded-[8px] overflow-hidden">
              {statItems.map((s, i) => (
                <div
                  key={s.label}
                  className={cn(
                    "p-3 flex flex-col gap-2",
                    i < statItems.length - 1 && "border-r border-border",
                  )}
                >
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground leading-tight">
                    {s.label}
                  </p>
                  <div className="text-lg font-semibold text-foreground tabular-nums inline-flex items-center gap-1">
                    {s.node ?? (
                      <>
                        {s.prefix && (
                          <span className="text-[10px] font-medium text-muted-foreground uppercase">{s.prefix}</span>
                        )}
                        <span>{s.value}</span>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Signal AI block */}
            <ThemeAnalysisBlock
              aiSummary={theme.ai_summary ?? null}
              keywordCount={theme.keywords.length}
              isRegenerating={isRegenerating}
              onRegenerate={() => regenerateAnalysis({ productId, themeId: theme.id })}
            />

            {/* Keywords */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-regular inline-flex items-center gap-1.5">
                    Keywords <CountBadge size="sm" count={theme.keywords.length} />
                  </p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-9 text-sm gap-1"
                  onClick={onAddKeyword}
                >
                  <Plus className="h-3 w-3" />
                  Add keyword
                </Button>
              </div>

              <div className="rounded-md border border-border overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="px-3">Keyword</TableHead>
                      <TableHead className="w-[60px] text-center px-2">
                        Rank
                      </TableHead>
                      <TableHead className="w-[50px] text-center px-2">
                        KPS
                      </TableHead>
                      <TableHead className="w-[90px] text-center px-2">
                        Search Vol.
                      </TableHead>
                      <TableHead className="w-[50px] text-center px-2">
                        Rel.
                      </TableHead>
                      <TableHead className="w-[60px] text-center px-2">
                        Trend
                      </TableHead>
                      <TableHead className="w-[36px] px-1" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {theme.keywords.map((kw) => (
                      <TableRow key={kw.keyword_id}>
                        <TableCell className="px-3">
                          <div className="flex flex-col gap-0.5 items-start">
                            <span className="text-sm">
                              {kw.search ?? `#${kw.keyword_id}`}
                            </span>
                            {kw.is_head_term && (
                              <span className="inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-medium text-primary whitespace-nowrap">
                                Head term
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-center px-2">
                          <RankBadge rank={kw.rank} />
                        </TableCell>
                        <TableCell className="text-center px-2 text-xs">
                          <KeywordMetricValue value={kw.kps} />
                        </TableCell>
                        <TableCell className="text-center px-2 text-xs">
                          {kw.search_volume != null ? (
                            formatMetricNumber(kw.search_volume)
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-center px-2 text-xs">
                          <KeywordMetricValue value={kw.hero_relevance} />
                        </TableCell>
                        <TableCell className="text-center px-2">
                          <TrendCell trend={kw.search_volume_trend} />
                        </TableCell>
                        <TableCell className="px-1 text-right">
                          {pendingKeywordId === kw.keyword_id ? (
                            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground ml-auto" />
                          ) : (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7"
                                  disabled={pendingKeywordId !== null}
                                >
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                {kw.is_head_term ? (
                                  <DropdownMenuItem
                                    onClick={() =>
                                      handleUnsetHeadTerm(kw.keyword_id)
                                    }
                                  >
                                    <Crown className="h-4 w-4 mr-2" />
                                    Remove as head term
                                  </DropdownMenuItem>
                                ) : (
                                  <DropdownMenuItem
                                    onClick={() =>
                                      handleSetHeadTerm(kw.keyword_id)
                                    }
                                  >
                                    <Crown className="h-4 w-4 mr-2" />
                                    Make head term
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  onClick={() =>
                                    handleRemoveKeyword(kw.keyword_id)
                                  }
                                  className="text-destructive"
                                >
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  Remove from theme
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                    {theme.keywords.length === 0 && (
                      <TableRow>
                        <TableCell
                          colSpan={7}
                          className="text-center text-sm text-muted-foreground py-8"
                        >
                          No keywords yet.{" "}
                          <button
                            onClick={onAddKeyword}
                            className="text-primary hover:underline"
                          >
                            Add one
                          </button>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      <DeleteThemeDialog
        theme={confirmDelete ? theme : null}
        onConfirm={handleDeleteTheme}
        onCancel={() => setConfirmDelete(false)}
      />
    </>
  );
}
